// src/hooks/useRoles.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RoleService } from '../admin/pages/roles/roleService';
import { RoleFormData } from '../admin/pages/roles/role';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────────────────────
export const ROLE_KEYS = {
  all: ['roles'] as const,
  lists: () => ['roles', 'list'] as const,
  paginated: (page: number, search: string) =>
    ['roles', 'list', page, search] as const,
  stats: () => ['roles', 'stats'] as const,
  allRoles: () => ['roles', 'all'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface RolePaginationParams {
  page: number;
  search: string;
  pageSize: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// useRolesPaginated
// ─────────────────────────────────────────────────────────────────────────────
export const useRolesPaginated = (params: RolePaginationParams) => {
  return useQuery({
    queryKey: ROLE_KEYS.paginated(params.page, params.search),
    queryFn: () =>
      RoleService.getRolesPaginated(params.pageSize, params.page, params.search),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useRoleStats
// ─────────────────────────────────────────────────────────────────────────────
export const useRoleStats = () => {
  return useQuery({
    queryKey: ROLE_KEYS.stats(),
    queryFn: () => RoleService.getRoleStats(),
    staleTime: 1000 * 60 * 5,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useAllRoles  (for dropdowns / pickers in other components)
// ─────────────────────────────────────────────────────────────────────────────
export const useAllRoles = () => {
  return useQuery({
    queryKey: ROLE_KEYS.allRoles(),
    queryFn: () => RoleService.getAllRoles(),
    staleTime: 1000 * 60 * 5,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useRoleMutations
// ─────────────────────────────────────────────────────────────────────────────
export const useRoleMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ROLE_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({
      formData,
      userId,
    }: {
      formData: RoleFormData;
      userId: string;
    }) => RoleService.createRole(formData, userId),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({
      roleId,
      formData,
    }: {
      roleId: string;
      formData: RoleFormData;
    }) => RoleService.updateRole(roleId, formData),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (roleId: string) => RoleService.deleteRole(roleId),
    onSuccess: invalidateAll,
  });

  return { create, update, remove };
};